/**
 * Ticket Detail Page
 * Complete ticket view with sidebar, comments, and activity
 */

import React, { useState } from 'react';
import apiClient from '../lib/api-client';
import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Edit2,
  Users,
  Calendar,
  Clock,
  Tag,
  MoreHorizontal,
  Paperclip,
  Download,
  Trash2,
} from 'lucide-react';
import { useTicket, useUpdateTicket } from '@/hooks';
import { TicketStatus, TicketPriority } from '@/types';
import { Badge } from '@/components/common/Badge';
import { Avatar } from '@/components/common/Avatar';
import { LoadingState } from '@/components/common/LoadingSpinner';
import { ErrorState } from '@/components/common/ErrorState';
import { CommentSection } from '@/components/comments/CommentSection';
import {
  getStatusColor,
  getPriorityColor,
  getTypeIcon,
  formatLabel,
  formatDateTime,
  formatRelativeTime,
} from '@/utils/helpers';

function TicketDetailPage() {
  const { ticketId } = useParams<{ ticketId: string }>();
  const navigate = useNavigate();
  const { data: ticket, isLoading, error, refetch } = useTicket(ticketId);
  const { mutate: updateTicket } = useUpdateTicket(ticketId!);
  const { data: members = [] } = useQuery({
    queryKey: ['project-members', ticket?.project?.id],
    queryFn: async () => {
      if (!ticket?.project?.id) return [];
      const res = await apiClient.get('/projects/' + ticket.project.id + '/members');
      return res.data;
    },
    enabled: !!ticket?.project?.id,
  });

  const [isEditing, setIsEditing] = useState(false);
  const [attachments, setAttachments] = React.useState<any[]>([]);
  const [uploading, setUploading] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const fetchAttachments = React.useCallback(async () => {
    if (!ticketId) return;
    try { const res = await apiClient.get('/tickets/' + ticketId + '/attachments'); setAttachments(res.data || []); }
    catch (err) { console.error('Failed to fetch attachments:', err); }
  }, [ticketId]);
  React.useEffect(() => { fetchAttachments(); }, [fetchAttachments]);
  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file || !ticketId) return;
    setUploading(true);
    try { const fd = new FormData(); fd.append('file', file);
      await apiClient.post('/tickets/' + ticketId + '/attachments', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      fetchAttachments();
    } catch (err) { console.error('Upload failed:', err); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };
  const handleDeleteAttachment = async (aid: string) => {
    if (!window.confirm('Delete this attachment?')) return;
    try { await apiClient.delete('/attachments/' + aid); fetchAttachments(); }
    catch (err) { console.error('Delete failed:', err); }
  };
  const fmtSize = (b: number) => b < 1024 ? b+' B' : b < 1048576 ? (b/1024).toFixed(1)+' KB' : (b/1048576).toFixed(1)+' MB';

  if (isLoading) {
    return <LoadingState message="Loading ticket..." />;
  }

  if (error || !ticket) {
    return (
      <ErrorState
        message={error instanceof Error ? error.message : 'Ticket not found'}
        retry={() => refetch()}
      />
    );
  }

  const handleStatusChange = (status: TicketStatus) => {
    updateTicket({ status });
  };

  const handlePriorityChange = (priority: TicketPriority) => {
    updateTicket({ priority });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(-1)}
                className="text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div className="flex items-center space-x-2">
                <span className="text-2xl">{getTypeIcon(ticket.type)}</span>
                <h1 className="text-2xl font-bold text-gray-900">
                  {ticket.ticket_key}
                </h1>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <button
                onClick={() => setIsEditing(!isEditing)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <Edit2 className="h-4 w-4 mr-2" />
                Edit
              </button>
              <button className="inline-flex items-center p-2 border border-gray-300 rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50">
                <MoreHorizontal className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Title and badges */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-start justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900 flex-1">
                  {ticket.title}
                </h2>
                <div className="flex items-center space-x-2 ml-4">
                  <select
                    value={ticket.priority}
                    onChange={(e) =>
                      handlePriorityChange(e.target.value as TicketPriority)
                    }
                    className={`text-xs font-medium border rounded px-2 py-1 ${getPriorityColor(
                      ticket.priority
                    )}`}
                  >
                    {Object.values(TicketPriority).map((p) => (
                      <option key={p} value={p}>
                        {formatLabel(p)}
                      </option>
                    ))}
                  </select>
                  <select
                    value={ticket.status}
                    onChange={(e) =>
                      handleStatusChange(e.target.value as TicketStatus)
                    }
                    className={`text-xs font-medium border rounded px-2 py-1 ${getStatusColor(
                      ticket.status
                    )}`}
                  >
                    {Object.values(TicketStatus).map((s) => (
                      <option key={s} value={s}>
                        {formatLabel(s)}
                      </option>
                    ))}
                  </select>
                  {ticket.status !== 'cancelled' && (
                    <button
                      onClick={() => {
                        if (window.confirm('Cancel this ticket? This will remove it from active views.')) {
                          updateTicket({ status: 'cancelled' });
                        }
                      }}
                      className="text-xs font-medium text-red-500 hover:text-red-700 border border-red-200 rounded px-2 py-1 hover:bg-red-50"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>

              {/* Description */}
              {ticket.description && (
                <div className="prose max-w-none">
                  <p className="text-gray-700 whitespace-pre-wrap">
                    {ticket.description}
                  </p>
                </div>
              )}

              {/* Tags */}
              {ticket.tags.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {ticket.tags.map((tag) => (
                    <span
                      key={tag.id}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
                      style={{
                        backgroundColor: `${tag.color}20`,
                        color: tag.color,
                        borderColor: `${tag.color}40`,
                        borderWidth: '1px',
                      }}
                    >
                      <Tag className="h-3 w-3 mr-1" />
                      {tag.name}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Comments */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <CommentSection ticketId={ticket.id} />
            </div>

            {/* Activity */}
            {ticket.activity.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Activity</h3>
                <div className="space-y-3">
                  {ticket.activity.slice(0, 10).map((activity) => (
                    <div key={activity.id} className="flex items-start space-x-3 text-sm">
                      <div className="flex-shrink-0">
                        {activity.user ? (
                          <Avatar
                            name={activity.user.name}
                            src={activity.user.avatar_url}
                            size="sm"
                          />
                        ) : (
                          <div className="h-6 w-6 rounded-full bg-gray-300" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="text-gray-700">
                          <span className="font-medium">
                            {activity.user?.name || 'System'}
                          </span>{' '}
                          {activity.action}
                          {activity.field_name && (
                            <>
                              {' '}
                              <span className="font-medium">{activity.field_name}</span>
                              {activity.old_value && activity.new_value && (
                                <>
                                  {' '}
                                  from <Badge>{activity.old_value}</Badge> to{' '}
                                  <Badge>{activity.new_value}</Badge>
                                </>
                              )}
                            </>
                          )}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {formatRelativeTime(activity.created_at)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Details Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Details</h3>
              <dl className="space-y-4">
                {/* Assignee */}
                <div>
                  <dt className="text-sm font-medium text-gray-500 flex items-center mb-1">
                    <Users className="h-4 w-4 mr-1" />
                    Assignee
                  </dt>
                  <dd>
                    <select
                      value={ticket.assignee_id || ticket.assignee?.id || ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        updateTicket({ assignee_id: val || null });
                      }}
                      className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 bg-white"
                    >
                      <option value="">Unassigned</option>
                      {members.map((m: any) => (
                        <option key={m.user_id} value={m.user_id}>
                          {m.user?.first_name ? m.user.first_name + ' ' + (m.user?.last_name || '') : m.user?.name || 'User'}
                        </option>
                      ))}
                    </select>
                  </dd>
                </div>

                {/* Reporter */}
                <div>
                  <dt className="text-sm font-medium text-gray-500 mb-1">Reporter</dt>
                  <dd>
                    {ticket.reporter && (
                      <div className="flex items-center space-x-2">
                        <Avatar
                          name={ticket.reporter.name}
                          src={ticket.reporter.avatar_url}
                          size="sm"
                        />
                        <span className="text-sm text-gray-900">
                          {ticket.reporter.name}
                        </span>
                      </div>
                    )}
                  </dd>
                </div>

                {/* Project */}
                <div>
                  <dt className="text-sm font-medium text-gray-500 mb-1">Project</dt>
                  <dd>
                    <Link
                      to={`/projects/${ticket.project.id}`}
                      className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                    >
                      {ticket.project.name}
                    </Link>
                  </dd>
                </div>

                {/* Created */}
                <div>
                  <dt className="text-sm font-medium text-gray-500 flex items-center mb-1">
                    <Calendar className="h-4 w-4 mr-1" />
                    Created
                  </dt>
                  <dd className="text-sm text-gray-900">
                    {formatDateTime(ticket.created_at)}
                  </dd>
                </div>

                {/* Updated */}
                <div>
                  <dt className="text-sm font-medium text-gray-500 flex items-center mb-1">
                    <Clock className="h-4 w-4 mr-1" />
                    Updated
                  </dt>
                  <dd className="text-sm text-gray-900">
                    {formatRelativeTime(ticket.updated_at)}
                  </dd>
                </div>

                {/* Story Points */}
                {ticket.story_points && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500 mb-1">
                      Story Points
                    </dt>
                    <dd className="text-sm text-gray-900">{ticket.story_points}</dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Attachments */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><Paperclip className="h-5 w-5" />Attachments ({attachments.length})</h3>
                <div><input ref={fileInputRef} type="file" onChange={handleUpload} className="hidden" /><button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 disabled:opacity-50">{uploading ? 'Uploading...' : '+ Add File'}</button></div>
              </div>
              {attachments.length > 0 ? (<div className="space-y-2">{attachments.map((att: any) => (
                <div key={att.id} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 min-w-0"><Paperclip className="h-4 w-4 text-gray-400 flex-shrink-0" /><div className="min-w-0"><p className="text-sm font-medium text-gray-900 truncate">{att.filename}</p><p className="text-xs text-gray-500">{fmtSize(att.file_size)} · {att.uploaded_by}</p></div></div>
                  <div className="flex items-center gap-1 flex-shrink-0"><a href={`/api/v1/tickets/${ticketId}/attachments/${att.id}/download`} className="p-1 text-gray-400 hover:text-primary-600" title="Download"><Download className="h-4 w-4" /></a><button onClick={() => handleDeleteAttachment(att.id)} className="p-1 text-gray-400 hover:text-red-600" title="Delete"><Trash2 className="h-4 w-4" /></button></div>
                </div>))}</div>) : (<p className="text-sm text-gray-400 text-center py-4">No attachments yet</p>)}
            </div>

            {/* Watchers */}
            {ticket.watchers.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Watchers ({ticket.watchers.length})
                </h3>
                <div className="space-y-2">
                  {ticket.watchers.map((watcher) => (
                    <div key={watcher.id} className="flex items-center space-x-2">
                      <Avatar
                        name={watcher.name}
                        src={watcher.avatar_url}
                        size="sm"
                      />
                      <span className="text-sm text-gray-900">{watcher.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default TicketDetailPage;
